import React from 'react';
import {
    Platform,
    StyleProp,
    StyleSheet,
    Text,
    View,
    useWindowDimensions,
    ViewStyle,
} from 'react-native';
import { useRouter } from 'expo-router';
import Button from '../ui/Button';
import Card from '../ui/Card';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { trackFunnelEvent } from '../../lib/funnelEvents';
import { msg } from '@lingui/core/macro';
import { type LocalizedMessage, useResolvedMessage } from '../../lib/i18n/messages';

interface CreateFeedLikeThisCardProps {
    projectId?: string | null;
    entrySurface: string;
    title?: LocalizedMessage;
    description?: LocalizedMessage;
    buttonLabel?: LocalizedMessage;
    style?: StyleProp<ViewStyle>;
    useTemplate?: boolean;
}

export default function CreateFeedLikeThisCard({
    projectId,
    entrySurface,
    title = msg({ id: 'feedLikeThis.title', message: 'Create a Feed Like This' }),
    description = msg({ id: 'feedLikeThis.description', message: 'Set up your own paper feed based on this topic, then customize the keywords, categories, and delivery options.' }),
    buttonLabel = msg({ id: 'feedLikeThis.getStarted', message: 'Get Started' }),
    style,
    useTemplate = true,
}: CreateFeedLikeThisCardProps) {
    const router = useRouter();
    const { fontsLoaded } = useFontLoading();
    const resolvedTitle = useResolvedMessage(title);
    const resolvedDescription = useResolvedMessage(description);
    const { width } = useWindowDimensions();
    const isDesktop = width >= 820;

    const handlePress = () => {
        const isTemplateFlow = Boolean(useTemplate && projectId);

        trackFunnelEvent('journey_started', {
            entry_surface: entrySurface,
            entry_path: typeof window !== 'undefined'
                ? `${window.location.pathname}${window.location.search}`
                : '/digest',
            template_project_id: isTemplateFlow ? projectId : null,
            scenario_candidate: isTemplateFlow ? 'template_to_form' : 'chat_to_form',
        });

        if (isTemplateFlow) {
            router.push(`/?openChat=true&templateProjectId=${projectId}&entrySurface=${entrySurface}`);
            return;
        }

        router.push(`/?openChat=true&entrySurface=${entrySurface}`);
    };

    return (
        <Card style={[styles.card, isDesktop && styles.cardDesktop, style]}>
            <View style={[styles.layout, isDesktop && styles.layoutDesktop]}>
                <View style={styles.contentBlock}>
                    <Text style={[styles.title, isDesktop && styles.titleDesktop, fontsLoaded && interSemiBold]}>
                        {resolvedTitle}
                    </Text>
                    <Text style={[styles.description, isDesktop && styles.descriptionDesktop, fontsLoaded && interRegular]}>
                        {resolvedDescription}
                    </Text>
                </View>

                <View style={[styles.actionWell, isDesktop && styles.actionWellDesktop]}>
                    <Button
                        label={buttonLabel}
                        variant="primary"
                        style={[styles.button, isDesktop && styles.buttonDesktop]}
                        textStyle={styles.buttonText}
                        onPress={handlePress}
                        testID="create-feed-like-this-button"
                    />
                </View>
            </View>
        </Card>
    );
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: '#f8fcf4',
        borderColor: '#d8e8c8',
        borderRadius: 20,
        padding: 24,
        marginBottom: 20,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#0b1020',
            shadowOpacity: 0.05,
            shadowRadius: 18,
            shadowOffset: { width: 0, height: 8 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 12px 28px rgba(11,16,32,0.05)',
        }),
    },
    cardDesktop: {
        paddingVertical: 20,
        paddingHorizontal: 24,
    },
    layout: {
        gap: 18,
    },
    layoutDesktop: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
    },
    contentBlock: {
        flex: 1,
    },
    title: {
        fontSize: 28,
        lineHeight: 34,
        color: colors.text900,
        marginBottom: 10,
    },
    titleDesktop: {
        fontSize: 26,
        lineHeight: 31,
        marginBottom: 8,
    },
    description: {
        fontSize: 16,
        lineHeight: 25,
        color: colors.text700,
        maxWidth: 680,
    },
    descriptionDesktop: {
        maxWidth: 620,
        lineHeight: 24,
    },
    actionWell: {
        alignItems: 'flex-start',
    },
    actionWellDesktop: {
        alignItems: 'flex-end',
        justifyContent: 'center',
        paddingLeft: 24,
        marginLeft: 8,
        borderLeftWidth: 1,
        borderLeftColor: '#dfe8d5',
    },
    button: {
        alignSelf: 'flex-start',
        minHeight: 46,
        borderRadius: 12,
        paddingHorizontal: 20,
        paddingVertical: 12,
    },
    buttonDesktop: {
        alignSelf: 'auto',
        minWidth: 168,
    },
    buttonText: {
        fontSize: 15,
    },
});
